import { Info } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"

export interface WizardAddressPrefilledNoticeProps {
  /** Brand name interpolated into the helper copy. */
  brand_name: string
  className?: string
}

/**
 * WizardAddressPrefilledNotice — informational banner above the address
 * fieldset on the Wizard Address step when the creator's globally-stored
 * `ShippingAddress` is pre-filled into the form (typical second-brand
 * path).
 *
 * **Accessibility:** uses `<Alert>` with the default informational variant
 * — NOT `role="alert"`. The notice is informational, not an error/urgent
 * announcement; raising it as `role="alert"` would interrupt SR users and
 * mis-categorise the affordance. We rely on the visual Info icon + heading
 * to surface intent. (UX Spec §7 + FE TRD v2 §Components.)
 *
 * **i18n:** copy lives at `wizard.address.prefilled_notice.{heading,body}`;
 * brand name interpolation is handled here.
 */
export function WizardAddressPrefilledNotice({
  brand_name,
  className,
}: WizardAddressPrefilledNoticeProps) {
  const { t } = useTranslation()
  return (
    <Alert
      // Override shadcn's default `role="alert"` — this is informational,
      // not urgent. `role="status"` is the polite-region equivalent (UX
      // Spec §7 a11y plan + FE TRD v2 §Components).
      role="status"
      data-testid="wizard-address-prefilled-notice"
      className={className}
    >
      <Info className="size-4" aria-hidden="true" />
      <AlertTitle data-testid="wizard-address-prefilled-notice-heading">
        {t("wizard.address.prefilled_notice.heading")}
      </AlertTitle>
      <AlertDescription data-testid="wizard-address-prefilled-notice-body">
        {t("wizard.address.prefilled_notice.body", { brand: brand_name })}
      </AlertDescription>
    </Alert>
  )
}

export default WizardAddressPrefilledNotice
